import { Location } from "@angular/common";
import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, AsyncValidatorFn, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BaseComponent } from 'app/base/base.component';
import { Country } from 'app/core/models/country';
import { Merchant } from 'app/core/models/merchant';
import { ContractService } from 'app/core/services/contract.service';
import { MerchantService } from 'app/core/services/merchant.service';
import { NotifMessageService } from 'app/core/services/notifmessage.service';
import { SupplierService } from 'app/core/services/supplier.service';
import * as moment from 'moment';
import { NGXLogger } from 'ngx-logger';
import { forkJoin, Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthenticatedRouteGuard } from 'security/authenticatedRouteGuard.service';

@Component({
  selector: 'app-supplier-crud',
  templateUrl: './supplier-crud.component.html'
})

/**
 * This component handles the display of supplier create, edit and details pages.
 * This is achieved three booleans determining when each user page is displayed.
 * Note: the pages are distinct for the user but handled with the same html template in the code.
 * 
 * @implements {OnInit, OnDestroy}
 * @extends {BaseComponent}
 */
export class SupplierCrudComponent extends BaseComponent implements OnInit, OnDestroy {

  isCreateMode: boolean;
  isEditMode: boolean;
  isDetailsMode: boolean;

  isLoading: boolean = false;

  countryItems: Array<Country> = Array<Country>();
  statusItems: Array<String> = new Array<String>();

  supplierForm: FormGroup;

  merchants = new Array<Merchant>();
  
  allMerchants = new Array<Merchant>();
  allMerchantsInit = new Array<Merchant>();
  merchantControl: FormControl = new FormControl();
  isMerchantAdding: boolean = false;


  subRoute: Subscription;
  merchantSub: Subscription;
  modalResponseSubscription: Subscription;

  isSaveNeeded: boolean;

  supplierInput: any;
  supplierId: any;

  merchantsId: Array<any> = new Array<any>();
  contactFormIsValid: boolean = true;

  contracts: any;
  contactInfo: any;

  bool : Array<boolean> = new Array(this.merchants.length).fill(0);

  constructor(
    private log: NGXLogger,
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private supplierService: SupplierService,
    private merchantService: MerchantService,
    private contractService: ContractService,
    public router: Router,
    private location: Location,
    private notifService: NotifMessageService,
    private roleGuard: AuthenticatedRouteGuard,
    private translateService: TranslateService
  ) { super(router) }


  ngOnInit(): void {
    this.statusItems = this.getStatus();
    this.countryItems = this.getCountrys();

    this.supplierId = this.route.snapshot.params['id'];
    this.isCreateMode = !this.supplierId;

    if(this.isCreateMode){
      this.initForm(this.isCreateMode);
    } else {
      this.getSupplier(this.supplierId);
      if (this.router.url.startsWith("/service/supplier/edit")) {
        this.isEditMode = true
        this.isSaveNeeded = this.supplierService.isSaveNeeded = false;
        this.supplierService.supplierSubject.subscribe(isSaveNeededTab => {
          this.isSaveNeeded = isSaveNeededTab[0];
        });
      } else {
        this.isDetailsMode = true
      }  
    }
  }
  
  initForm(isCreateMode : boolean) {
    this.supplierForm = this.formBuilder.group({
      name: new FormControl(isCreateMode ? '' : this.supplierInput.name, Validators.required),
      externalRef:new FormControl(isCreateMode ? '' : this.supplierInput.externalRef, [Validators.required], [this.externalRefValidator()]),
      status: new FormControl(isCreateMode ? '' : this.supplierInput.status, Validators.required),
    });

    if(!isCreateMode){
      let supplierGlobalInfoInit = {
        id: this.supplierInput.id,
        name: this.supplierInput.name,
        externalRef: this.supplierInput.externalRef,
        status: this.supplierInput.status,
        createdDate: this.supplierInput.createdDate,
      }
      this.supplierService.supplierGlobalInfo = supplierGlobalInfoInit;
  
      this.supplierForm.valueChanges.subscribe(val => {
  
        let newSupplierGlobalInfo = {
          id: this.supplierInput.id,
          name: val.name,
          externalRef: this.supplierInput.externalRef,
          status: val.status,
          createdDate: this.supplierInput.createdDate,
        }
  
        this.supplierService.setSupplierGlobalInfo(newSupplierGlobalInfo);
      });
    }
  }

  get supplier() {
    return this.supplierForm.controls;
  }

  onSubmitForm() {
    this.contactInfo = this.supplierService.getSupplierContactInfo()
    let newSupplier = {
      name: this.supplierForm.get('name').value,
      externalRef: this.supplierForm.get('externalRef').value,
      status: this.supplierForm.get('status').value,
      posIdList: [],
      creationDate: moment().format("DD/MM/YYYY hh:mm:ss"),
      merchants: [],
      address: {
        address_line1: this.contactInfo.address.address_line1,
        address_line2: this.contactInfo.address.address_line2,
        address_line3: this.contactInfo.address.address_line3,
        zip_code: this.contactInfo.address.zip_code,
        city: this.contactInfo.address.city,
        country: this.contactInfo.address.country
      },
      contact: {
        firstName: this.contactInfo.contact.firstName,
        lastName: this.contactInfo.contact.lastName,
        tel: this.contactInfo.contact.tel,
        email: this.contactInfo.contact.email
      }
    };
    //check if the supplier contact exist && check id externalRef exists
    forkJoin([this.supplierService.isEmailExists(newSupplier.contact.email),
      this.supplierService.isExternalRefExists(newSupplier.externalRef)]).subscribe(
        ([resEmail, resExternalRef]) => {
          if (resEmail) {
            this.notifService.emitModal('CONTACT_EXISTS');
          }
          if (resExternalRef) {
            this.notifService.emitModal('EXTERNAL_REF_EXISTS');
          }
          if (!resEmail && !resExternalRef) {
            this.supplierService.createSupplier(newSupplier).subscribe(
              supp => {
                if (supp != null) {
                  for (let i = 0; i < this.merchants.length; i++) {
                    let newContract = {
                      supplierId: supp.id,
                      merchantId: this.merchants[i].id,
                      discountEnabled : this.bool[this.merchants[i].id]
                    }
                    this.contractService.createContractAPI(newContract).subscribe();
                  }
                  this.router.navigate(['/service/supplier'])
                  this.notifService.notifySuccess('SUPPLIER_CREATED');
                }
              }
            );
          }
        }
      );
  }

  getSupplier(id) {
    this.isLoading = true;
    this.supplierService.getSupplierById(id).subscribe(
      (supplier) => {
        this.supplierInput = supplier;
        if (this.isEditMode) {
          this.initForm(this.isCreateMode);
        }
        if (this.roleGuard.isEnabled('CONTRACT_READ') && this.supplierInput.id !== undefined) {
          this.getContracts();
        } else {
          this.isLoading = false;
        }
      }
    );
  }

  getContracts() {
    this.isLoading = true;
    let criteria = {
      supplierId: this.supplierId
    }
    this.contractService.findByCriteria(criteria).subscribe(
      contracts => {
        this.contracts = contracts.content;
        if (this.roleGuard.isEnabled('MERCHANT_READ')) {
          this.getAllMerchants().subscribe();
        } else {
          this.isLoading = false;
        }
      }
    );
  }

  getAllMerchants(): Observable<any> {
    this.merchantsId = new Array<any>();
    return this.merchantService.getAllMerchants().pipe(map(merchants => {
      this.allMerchants = merchants.content;
      this.allMerchantsInit = merchants.content;
      this.merchantControl = new FormControl(this.allMerchants[0].id);

      this.merchants.splice(0, this.merchants.length);
      if (this.contracts !== undefined && this.contracts.length > 0) {
        this.contracts.forEach(contract => {
          this.isLoading = true;
          this.merchantsId.push(contract.merchantId);
        });
        this.getMerchants();
      } else {
        this.isLoading = false;
      }
    }));
  }

  getMerchants() {
    this.merchantService.getMerchantsByIds(this.merchantsId).subscribe(merchants => {
      this.merchants = merchants;
      let listContract : any[] = new Array<any>();
      this.contracts.forEach(element => {
        listContract.push(
          {discountEnabled : element.discountEnabled,
          merchantId : element.merchantId}
        )
      });
      this.merchants.forEach(merchant => Object.assign(merchant, listContract.find(contract => contract.merchantId == merchant.id) ));
      this.addMerchant();
    });
  }

  addMerchant() {
    let indexTab: Array<number> = [];
    this.allMerchants = Object.assign([], this.allMerchantsInit);
    this.allMerchants.forEach(merchant => {
      if (this.merchants.some(mer => mer.id == merchant.id)) {
        indexTab.push(this.allMerchants.findIndex(mer => mer.id == merchant.id));
      }
    });
    this.allMerchants = this.allMerchants.filter((mer, i) => !indexTab.includes(i));
    if(this.allMerchants.length !== 0) {
      this.merchantControl = new FormControl(this.allMerchants[0].id);
    }
    this.isLoading = false;
  }
  
  addContract() {
    if (this.merchantControl.value != null && this.merchantControl.value != "undefined") {
      let newContract = {
        supplierId: this.supplierId,
        merchantId: this.merchantControl.value
      };
      this.contractService.createContractAPI(newContract).subscribe(res => {
        if (res != null) {
          this.getContracts();
          this.notifService.notifySuccess('CONTRACT_CREATED');
        }
      });
    }
  }

  deleteContract(merchantId) {
    this.notifService.emitModal("DELETE_CONTRACT");

    this.modalResponseSubscription = this.notifService.modalResponseSubject$.subscribe(resp => {

      if (resp) {
        this.contractService.deleteContractByMerchantIdAndSupplierId(merchantId, this.supplierId).subscribe(res => {
          this.getContracts();
          this.notifService.notifySuccess('CONTRACT_DELETED');
        });
      }
      this.modalResponseSubscription.unsubscribe();
    });
  }

  canDeactivate(): Observable<boolean> | boolean {

    if (this.isSaveNeeded) {
      this.notifService.emitModal("SAVE_NEEDED");
      return this.notifService.modalResponseSubject$;
    } else {
      return true;
    }
  }

  ngOnDestroy() {
    if (this.subRoute) {
      this.subRoute.unsubscribe();
    }
    if (this.merchantSub) {
      this.merchantSub.unsubscribe();
    }
  }

  save() {
    this.supplierService.updateSupplier().subscribe((res) => {
      if (res != null) {
        this.supplierService.isSaveNeeded = false;
        this.supplierService.supplierSubject.next([this.supplierService.isSaveNeeded]);
        this.notifService.notifySuccess('SAVED');
      }
    });
  }

  checkDuplication() {
    let criteria = {
      contact: {
        email: this.supplierService.supplierContactInfo.contact.email,
      }
    }

    if (criteria.contact.email === this.supplierInput.contact.email) {
      this.save();
      this.supplierInput.contact.email = criteria.contact.email;
      return;
    }

    this.supplierService.findByCriteria(criteria, {}, false).subscribe((supplier) => {
      if (supplier.content.length > 0 && supplier.content.filter(sup => sup.contact.email === criteria.contact.email).length > 0) {
        this.notifService.emitModal("CONTACT_EXISTS");
      } else {
        this.save();
        this.supplierInput.contact.email = criteria.contact.email;
      }
    });
  }

  back() {
    this.router.navigate(["/service/supplier"]);
  }

  disable(): boolean {
    if (this.supplierForm.valid && this.contactFormIsValid && this.isSaveNeeded) {
      return false;
    }
    return true;
  }

  checkIfContactFormIsValid($event) {
    this.contactFormIsValid = $event;
  }

  transSearchFn = (term: string, item: any) => {
    term = term.toLocaleLowerCase();
    return this.translateService.instant(item).toLocaleLowerCase().indexOf(term) > -1;
  }

  toggleEditable(merchantId){
    let contract = this.contracts.find((contract) => contract.merchantId == merchantId)
    contract.discountEnabled = !contract.discountEnabled; 
    this.contractService.updateContract(contract).subscribe(res => {
      this.notifService.notifySuccess('CONTRACT_UPDATED');
    });
  }

  edit(): void {
    this.router.navigate(['/service/supplier/edit', this.supplierId]);
  }

  /** async validator to check if there exists another supplier with the same external reference. */
  externalRefValidator() : AsyncValidatorFn {
    return (control: AbstractControl) : Observable<ValidationErrors> | null => {
      return this.supplierService.isExternalRefExists(control.value).pipe(
        map(externalRefExists => {
          if (this.supplierInput) {
            return externalRefExists && this.supplierInput.externalRef !== control.value ? {exists:true} : null;
          }
          return externalRefExists ? {exists:true} : null;
        }
      ));
    }
  }
}
